'use client';

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Ref
} from 'react';

import type {
  MathfieldElement,
  VirtualKeyboardLayout
} from 'mathlive';

import {
  isKeyboardName,
  keyboardLayout
} from '@/app/lib/practice/keyboards';

import {
  templateForWidth
} from '@/app/lib/practice/template-fit';

import type {
  PracticeAnswerInput
} from '@/app/lib/practice/types';

// The practice answer box (spec: Brain elite-scholars/30-projects/
// practice-solving-ux-spec.md, section 3). A MathLive field with the
// question's own keyboard first; for template questions a read-only field
// whose \placeholder boxes are the only editable parts.
//
// The box never decides right or wrong. read() hands back the raw LaTeX (the
// field's value, or each box's value) for the attempts route, which
// normalises and checks it on the server; the per-box verdict comes back and
// is shown with setPromptState.
//
// Keyboard:
//   - touch (pointer: coarse): the maths keyboard opens when a box gets focus
//     and replaces the phone's own; its height is added to the page's bottom
//     padding and the bottom bar's offset (--practice-kb), and the box is
//     scrolled above it.
//   - desktop: typing works directly; a tip and a "Show keyboard" toggle.
//   - mathVirtualKeyboardPolicy 'sandboxed' in every case, shown and hidden
//     here: inside a frame MathLive's default only asks the top page for a
//     keyboard, and nothing appears (what broke the prototype on phones).
//
// MathLive is about 225 KB gzipped, so it is imported dynamically: only
// pages that render this box (and the maths tools) ever load it. If it
// fails, a plain text box is shown and the server checks plain text.

const BASE_PATH = (process.env.__NEXT_ROUTER_BASEPATH as string | undefined) ?? '';

/** The bottom bar's height above the keyboard, to keep the box clear of both. */
const BOTTOM_BAR_PX = 76;
/** The portal's fixed top navigation. */
const TOP_NAV_PX = 112;

export type AnswerPayload
  = | { format: 'latex'
    answer: string }
    | { format: 'latex'
      prompts: Record<string, string> }
      | { format: 'text'
        answer: string };

export interface AnswerBoxHandle {
  /** What is in the box, or null if nothing has been typed. */
  read: () => AnswerPayload | null
  focus: () => void
  /** Puts the on-screen maths keyboard away (after Check, so the verdict is visible). */
  hideKeyboard: () => void
}

interface Props {
  questionId: string
  input: PracticeAnswerInput | null
  ariaLabel: string
  /** Enter in the box. */
  onEnter: () => void
  /** The server's verdict per fill-in box; null clears the marks. */
  promptStates: Record<string, boolean> | null
  ref?: Ref<AnswerBoxHandle>
}

type Keyboard = Window['mathVirtualKeyboard'];

// Read fresh each time: MathLive replaces the global when it mounts the keyboard.
const keyboard = (): Keyboard | undefined => (typeof window === 'undefined' ? undefined : window.mathVirtualKeyboard);

function setKeyboardInset(px: number): void {

  document.documentElement.style.setProperty('--practice-kb', `${ Math.max(0, Math.round(px)) }px`);
}

export function PracticeAnswerBox({
  questionId, input, ariaLabel, onEnter, promptStates, ref
}: Props) {

  const hostRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<MathfieldElement | null>(null);
  const plainRef = useRef<HTMLInputElement | null>(null);
  const enterRef = useRef(onEnter);

  const [ ready, setReady ] = useState(false);
  const [ failed, setFailed ] = useState(false);
  const [ touch, setTouch ] = useState(false);
  const [ keyboardOpen, setKeyboardOpen ] = useState(false);
  const [ plainValue, setPlainValue ] = useState('');

  const template = input?.template ?? null;
  const keyboardName = input && isKeyboardName(input.keyboard) ? input.keyboard : 'expression';
  const keysKey = (input?.keys ?? []).join(' ');

  useEffect(() => {

    enterRef.current = onEnter;
  }, [ onEnter ]);

  const layout = useCallback((): (VirtualKeyboardLayout | 'numeric' | 'symbols' | 'alphabetic')[] => {

    const own = keyboardLayout(keyboardName, keysKey ? keysKey.split(' ') : []) as unknown as VirtualKeyboardLayout;

    return [ own, 'numeric', 'symbols', 'alphabetic' ];
  }, [ keyboardName, keysKey ]);

  const showKeyboard = useCallback(() => {

    const kb = keyboard();

    if (!kb) {

      return;
    }

    kb.layouts = layout();
    kb.show({
      animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    });
  }, [ layout ]);

  // ── Mount the field ───────────────────────────────────────────────────────
  useEffect(() => {

    let cancelled = false;
    let field: MathfieldElement | null = null;
    const coarse = window.matchMedia('(pointer: coarse)').matches;

    const onGeometry = () => {

      const kb = keyboard();
      const height = kb?.visible ? kb.boundingRect.height : 0;

      setKeyboardInset(height);
      setKeyboardOpen(Boolean(kb?.visible));

      if (height > 0 && field) {

        // Keep the box in view above the keyboard and the bottom bar.
        window.setTimeout(() => {

          if (!field) {

            return;
          }

          const rect = field.getBoundingClientRect();
          const limit = window.innerHeight - height - BOTTOM_BAR_PX;

          if (rect.bottom > limit || rect.top < TOP_NAV_PX) {

            window.scrollBy({
              top: rect.bottom - limit + 16,
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
            });
          }
        }, 60);
      }
    };

    import('mathlive')
      .then((ml) => {

        if (cancelled || !hostRef.current) {

          return;
        }

        // Our own copies of the KaTeX fonts (public/mathlive-fonts): the
        // package default resolves next to the bundle and 404s behind the
        // /portal basePath. No sounds.
        ml.MathfieldElement.fontsDirectory = `${ BASE_PATH }/mathlive-fonts`;
        ml.MathfieldElement.soundsDirectory = null;

        field = new ml.MathfieldElement();
        field.mathVirtualKeyboardPolicy = 'sandboxed';
        field.smartFence = true;
        field.className = 'practice-mathfield';
        field.setAttribute('aria-label', ariaLabel);
        field.setAttribute('data-question', questionId);

        if (template) {

          // Fill-in-the-blank: only the \placeholder boxes take input.
          // Narrower gaps between the boxes on a phone (template-fit.ts).
          field.readOnly = true;
          field.value = templateForWidth(template, hostRef.current.clientWidth);
        }

        field.addEventListener('focusin', () => {

          const kb = keyboard();

          if (kb) {

            kb.layouts = layout();
          }

          if (coarse) {

            showKeyboard();
          }
        });

        field.addEventListener('keydown', (event) => {

          if ((event as KeyboardEvent).key === 'Enter') {

            event.preventDefault();
            enterRef.current();
          }
        });

        hostRef.current.appendChild(field);
        fieldRef.current = field;
        setTouch(coarse);
        keyboard()?.addEventListener('geometrychange', onGeometry);
        keyboard()?.addEventListener('virtual-keyboard-toggle', onGeometry);
        setReady(true);
      })
      .catch(() => {

        if (!cancelled) {

          setFailed(true);
        }
      });

    return () => {

      cancelled = true;
      keyboard()?.removeEventListener('geometrychange', onGeometry);
      keyboard()?.removeEventListener('virtual-keyboard-toggle', onGeometry);

      if (keyboard()?.visible) {

        keyboard()?.hide();
      }

      setKeyboardInset(0);
      field?.remove();
      fieldRef.current = null;
    };
  }, [ questionId, template, ariaLabel, layout, showKeyboard ]);

  // ── The server's verdict on each box ──────────────────────────────────────
  useEffect(() => {

    const field = fieldRef.current;

    if (!field || !template || !ready) {

      return;
    }

    for (const id of field.getPrompts()) {

      const state = promptStates?.[id];

      field.setPromptState(id, state === undefined ? undefined : (state ? 'correct' : 'incorrect'));
    }
  }, [ promptStates, template, ready ]);

  useImperativeHandle(ref, () => ({
    read: () => {

      const field = fieldRef.current;

      if (failed || !field) {

        const text = plainRef.current?.value.trim() ?? '';

        return text ? {
          format: 'text',
          answer: text
        } : null;
      }

      if (template) {

        const prompts: Record<string, string> = {};

        for (const id of field.getPrompts()) {

          prompts[id] = field.getPromptValue(id);
        }

        return Object.values(prompts).some((v) => v.trim() !== '') ? {
          format: 'latex',
          prompts
        } : null;
      }

      const value = field.value.trim();

      return value ? {
        format: 'latex',
        answer: value
      } : null;
    },
    focus: () => {

      (fieldRef.current ?? plainRef.current)?.focus();
    },
    hideKeyboard: () => {

      // On a phone the keyboard covered the 'Correct, 3 of 3 marks' line
      // after Check (seen in the 9 Oct 2026 demo captures).
      if (keyboard()?.visible) {

        keyboard()?.hide();
      }

      fieldRef.current?.blur();
    }
  }), [ failed, template ]);

  const toggleKeyboard = () => {

    const kb = keyboard();

    if (!kb) {

      return;
    }

    if (kb.visible) {

      kb.hide();
    } else {

      fieldRef.current?.focus();
      showKeyboard();
    }
  };

  if (failed) {

    return (
      <div className="space-y-1.5">
        <input
          ref={plainRef}
          value={plainValue}
          onChange={(e) => setPlainValue(e.target.value)}
          onKeyDown={(e) => {

            if (e.key === 'Enter') {

              e.preventDefault();
              enterRef.current();
            }
          }}
          aria-label={ariaLabel}
          autoComplete="off"
          spellCheck={false}
          placeholder="Your answer"
          className="w-full min-h-[48px] px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-base font-mono text-[#111111] dark:text-white bg-white dark:bg-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(1,158,250,0.6)]"
        />
        <p className="text-xs text-gray-400">
          The maths keyboard could not load, so type in plain text: sqrt() for roots, ^ for powers, / for fractions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div ref={hostRef} className="practice-answer min-w-0" />
      {!ready && (
        <p className="text-xs text-gray-400">Loading the maths keyboard…</p>
      )}
      {ready && !touch && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed flex-1 min-w-[12rem]">
            Type on your keyboard:
            {' '}
            <code>sqrt</code>
            {' '}
            for √,
            {' '}
            <code>^</code>
            {' '}
            for powers,
            {' '}
            <code>/</code>
            {' '}
            for fractions,
            {' '}
            <code>&lt;=</code>
            {' '}
            for ≤.
            {' '}
            {template ? 'Tab moves between boxes. ' : ''}
            Enter checks.
          </p>
          <button
            type="button"
            onClick={toggleKeyboard}
            aria-pressed={keyboardOpen}
            className="min-h-[44px] px-4 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[rgba(1,158,250,0.5)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)]"
          >
            {keyboardOpen ? 'Hide keyboard' : 'Show keyboard'}
          </button>
        </div>
      )}
    </div>
  );
}
